import { useMap } from "@vis.gl/react-maplibre";
import { useEffect } from "react";
import useEvent from "react-use-event-hook";

import type { ICoordinate, IPoint } from "@ryde/shared";

import { getAliveMap } from "./maplibre-map.utils";
import { isWithinClickTolerance } from "./maplibre-place-info.utils";

/**
 * While place info is on, a click on the bare map picks that point, and Enter on the focused map
 * picks its centre — the keyboard way to the same popup.
 *
 * MapLibre hands one `click` to every listener with no way to claim it, so the click is stopped before
 * MapLibre's canvas listener sees it: a screen's own map click (move the address, add a waypoint, draw a
 * vertex) waits until the mode is off. Markers, popups and controls are not the canvas and pass untouched.
 */
export const useMapLibrePlaceInfoClicks = ({
  isActive,
  onPick,
}: {
  isActive: boolean;
  onPick: (coordinate: ICoordinate) => void;
}): void => {
  const { current: mapRef } = useMap();
  const handlePick = useEvent(onPick);

  useEffect(() => {
    const map = getAliveMap(mapRef);

    if (!map || !isActive) {
      return undefined;
    }

    const container = map.getContainer();
    const canvas = map.getCanvas();
    let pressPoint: IPoint | undefined;

    const handlePointerDown = (event: PointerEvent): void => {
      pressPoint =
        event.target === canvas
          ? { x: event.clientX, y: event.clientY }
          : undefined;
    };

    const handleClick = (event: MouseEvent): void => {
      if (event.target !== canvas) {
        return;
      }

      event.stopPropagation();

      const releasePoint: IPoint = { x: event.clientX, y: event.clientY };

      if (!isWithinClickTolerance({ pressPoint, releasePoint })) {
        return;
      }

      const { left, top } = canvas.getBoundingClientRect();
      // Wrapped: on a world copy panned to, MapLibre answers longitudes past ±180, which the lookup rejects.
      const { lat, lng } = map
        .unproject([releasePoint.x - left, releasePoint.y - top])
        .wrap();

      handlePick({ lat, lng });
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Enter" && event.target === canvas) {
        const { lat, lng } = map.getCenter().wrap();

        handlePick({ lat, lng });
      }
    };

    container.addEventListener("pointerdown", handlePointerDown, true);
    container.addEventListener("click", handleClick, true);
    container.addEventListener("keydown", handleKeyDown, true);

    return () => {
      container.removeEventListener("pointerdown", handlePointerDown, true);
      container.removeEventListener("click", handleClick, true);
      container.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [handlePick, isActive, mapRef]);
};
